import { useState, useEffect } from 'react';

/**
 * Anti-spam contact obfuscation.
 * Values are ROT13-encoded — decoded only at runtime in the browser.
 * Crawlers reading static HTML see gibberish; real users see the correct info.
 *
 * ROT13 of "contact@MAX-Sourcing.com" = "pbagnpg@ZNK-Fbhepvat.pbz"
 * ROT13 of "+1 819-923-0918"          = "+1 819-923-0918" (digits unchanged)
 * ROT13 of "+1 819-923-0918" letters only: no letters, so store as-is but split oddly
 */

const ROT13_EMAIL = 'pbagnpg@ZNK-Fbhepvat.pbz';
// Phone has no letters so we split it into chunks to prevent direct scraping
const PHONE_CHUNKS = ['+1 ', '819', '-92', '3-0', '918'];

function rot13(str: string): string {
  return str.replace(/[a-zA-Z]/g, (c) => {
    const base = c <= 'Z' ? 65 : 97;
    return String.fromCharCode(((c.charCodeAt(0) - base + 13) % 26) + base);
  });
}

interface Props {
  type: 'email' | 'phone';
  className?: string;
  children?: React.ReactNode;
}

export default function ObfuscatedContact({ type, className = '', children }: Props) {
  const [value, setValue] = useState('');

  useEffect(() => {
    if (type === 'email') {
      setValue(rot13(ROT13_EMAIL));
    } else {
      setValue(PHONE_CHUNKS.join(''));
    }
  }, [type]);

  if (!value) {
    return (
      <span className={className} aria-label={type === 'email' ? 'Email address' : 'Phone number'}>
        {children ?? '\u2022\u2022\u2022'}
      </span>
    );
  }

  if (type === 'email') {
    return (
      <a href={`mailto:${value}`} className={className}>
        {children ?? value}
      </a>
    );
  }

  const href = `tel:${value.replace(/[\s\-]/g, '')}`;
  return (
    <a href={href} className={className}>
      {children ?? value}
    </a>
  );
}
